import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { HttpService } from '../../game/services/HttpService';
import './ForgotPassword.component.css';
import logo from '../../assets/compressed/logoMainH-min.png';

type Step = 'email' | 'code';

const ForgotPassword: React.FC = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState<Step>('email');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [loading, setLoading] = useState(false);

  const httpService = new HttpService();

  const validateEmail = (emailVal: string): boolean =>
    /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(emailVal);

  const handleRequestCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');
    const emailNormalized = email.trim().toLowerCase();
    if (!validateEmail(emailNormalized)) {
      setError('Please enter a valid email address.');
      return;
    }
    setLoading(true);
    try {
      const response = await httpService.post<any>('user/resetPassword', { email: emailNormalized });
      const isSuccess = response?.status === 'success' || response?.message?.toLowerCase().includes('success');
      if (isSuccess) {
        setSuccessMessage('If an account exists for this email, you will receive a reset code. Check your inbox.');
        setStep('code');
      } else {
        setError(response?.message || 'Failed to send reset code.');
      }
    } catch (err: any) {
      setError('Unable to send reset code. Please check the email address or try again later.');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');
    const emailNormalized = email.trim().toLowerCase();
    const codeTrimmed = code.trim();
    if (!codeTrimmed) {
      setError('Please enter the code from your email.');
      return;
    }
    if (password.length < 5) {
      setError('Password must be at least 5 characters long.');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    setLoading(true);
    try {
      const response = await httpService.post<any>('user/confirmResetPassword', {
        email: emailNormalized,
        token: codeTrimmed,
        password,
      });
      const isSuccess = response?.status === 'success' || response?.message?.toLowerCase().includes('success');
      if (isSuccess) {
        setSuccessMessage('Password reset successfully. Redirecting to login...');
        setTimeout(() => navigate('/login', { state: { resetSuccess: true } }), 2000);
      } else {
        setError(response?.message || 'Failed to reset password.');
      }
    } catch (err: any) {
      const msg = err?.message || '';
      if (msg.toLowerCase().includes('not found') || msg.toLowerCase().includes('expired')) {
        setError('Invalid or expired code. Please request a new code.');
      } else {
        setError(msg || 'Failed to reset password. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="game-auth-container">
      <div className="game-auth-background" />
      <div className="game-auth-content">
        <motion.div
          className="game-auth-card forgot-password-card"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className="game-auth-header">
            <img className="header-logo" src={logo} alt="Wild Chips Poker" onClick={() => navigate('/')} />
            <p>{step === 'email' ? 'Forgot password' : 'Reset password'}</p>
          </div>

          {error && (
            <motion.div className="error-message" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
              {error}
            </motion.div>
          )}
          {successMessage && (
            <motion.div className="success-message" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
              {successMessage}
            </motion.div>
          )}

          <AnimatePresence mode="wait">
            {step === 'email' ? (
              <motion.form
                key="email"
                onSubmit={handleRequestCode}
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 10 }}
                transition={{ duration: 0.2 }}
              >
                <p className="forgot-password-hint">Enter your email and we&apos;ll send you a code to reset your password.</p>
                <div className="form-group">
                  <label>Email</label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    autoComplete="email"
                    placeholder="your@email.com"
                  />
                </div>
                <button type="submit" className="submit-button" disabled={loading}>
                  {loading ? 'Sending...' : 'Send reset code'}
                </button>
              </motion.form>
            ) : (
              <motion.form
                key="code"
                onSubmit={handleResetPassword}
                initial={{ opacity: 0, x: 10 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -10 }}
                transition={{ duration: 0.2 }}
              >
                <p className="forgot-password-hint">Enter the code from your email and choose a new password.</p>
                <div className="form-group">
                  <label>Reset code</label>
                  <input
                    type="text"
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    required
                    autoComplete="one-time-code"
                    placeholder="Paste the code from your email"
                  />
                </div>
                <div className="form-group password-with-toggle">
                  <label>New password</label>
                  <div className="password-input-wrap">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      minLength={5}
                      autoComplete="new-password"
                    />
                    <button
                      type="button"
                      className="password-toggle-btn"
                      onClick={() => setShowPassword((v) => !v)}
                      title={showPassword ? 'Hide password' : 'Show password'}
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                    >
                      {showPassword ? (
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M3.98 8.223A10.477 10.477 0 001.932 12c1.292 4.338 5.31 7.5 10.068 7.5.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.068 7.5a10.523 10.523 0 01-1.724 2.547m-3.75-3.75a10.5 10.5 0 01-4.5-4.5m4.5 4.5a10.5 10.5 0 01-4.5-4.5" />
                        </svg>
                      ) : (
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
                          <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                      )}
                    </button>
                  </div>
                </div>
                <div className="form-group password-with-toggle">
                  <label>Confirm new password</label>
                  <div className="password-input-wrap">
                    <input
                      type={showConfirmPassword ? 'text' : 'password'}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      required
                      minLength={5}
                      autoComplete="new-password"
                    />
                    <button
                      type="button"
                      className="password-toggle-btn"
                      onClick={() => setShowConfirmPassword((v) => !v)}
                      title={showConfirmPassword ? 'Hide password' : 'Show password'}
                      aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                    >
                      {showConfirmPassword ? (
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M3.98 8.223A10.477 10.477 0 001.932 12c1.292 4.338 5.31 7.5 10.068 7.5.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.068 7.5a10.523 10.523 0 01-1.724 2.547m-3.75-3.75a10.5 10.5 0 01-4.5-4.5m4.5 4.5a10.5 10.5 0 01-4.5-4.5" />
                        </svg>
                      ) : (
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
                          <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                      )}
                    </button>
                  </div>
                </div>
                <button type="submit" className="submit-button" disabled={loading}>
                  {loading ? 'Resetting...' : 'Reset password'}
                </button>
              </motion.form>
            )}
          </AnimatePresence>

          <div className="auth-switch">
            <button type="button" className="link-button" onClick={() => step === 'code' ? setStep('email') : navigate('/login')}>
              {step === 'code' ? 'Use a different email' : 'Back to login'}
            </button>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default ForgotPassword;
